import { ChangeDetectionStrategy, Component } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormControl, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ComplexDialogTemplateConfigurationService, DynamicDialogButtonType, InputV3Component } from '@gea/digital-ui-lib';
import { DataMaintenanceTableColumns } from '../data-maintenance.model';

@Component({
  selector: 'app-data-maintenance-dialog',
  imports: [InputV3Component, ReactiveFormsModule],
  templateUrl: './data-maintenance-dialog.component.html',
  styleUrl: './data-maintenance-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataMaintenanceDialogComponent {
  private destroy$ = new Subject<void>();
  maintenanceEntryForm = this.formBuilder.group({});
  columns: DataMaintenanceTableColumns[] = [];
  templateService!: ComplexDialogTemplateConfigurationService<unknown>;
  private numericTypes = ['int', 'int16', 'int32', 'int64', 'decimal', 'double', 'float', 'single', 'long', 'short', 'byte'];

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: DynamicDialogRef,
    private dialogConfig: DynamicDialogConfig
  ) {
    this.templateService = this.dialogConfig.data!.templateConfigService;
    this.templateService.setDisableAcceptButton(true);
    this.setColumnData();
    this.saveClicked();
  }

  setColumnData(): void {
    let tempColumns = [];
    if (this.dialogConfig.data) {
      tempColumns = this.dialogConfig?.data.columns;
      tempColumns.forEach((col: DataMaintenanceTableColumns) => {
        if (col.name && col.header) {
          const validators = this.buildValidators(col);
          this.maintenanceEntryForm.addControl(col.name, new FormControl('', validators));
          this.columns.push(col);
        }
      });

      if (this.dialogConfig.data.data) {
        this.maintenanceEntryForm.patchValue(this.dialogConfig.data.data);
      }
      this.enableAndDisableSave();
    }
  }

  buildValidators(col: DataMaintenanceTableColumns): ValidatorFn[] {
    const v: ValidatorFn[] = [];
    if (col.required) v.push(Validators.required);

    if (this.numericTypes.includes(col.type) && col.ranges) {
      if (typeof col.ranges !== 'string') {
        v.push(Validators.min(col.ranges.min), Validators.max(col.ranges.max));
      } else {
        const minMax = this.extractMinMax(col.ranges);
        if (minMax) {
          v.push(Validators.min(minMax.min), Validators.max(minMax.max));
        }
      }
    }

    return v;
  }

  extractMinMax(value: string): { min: number; max: number } | null {
    if (!value) return null;
    const parts = value.split('...');
    if (parts.length === 2) {
      const min = parseFloat(parts[0]);
      const max = parseFloat(parts[1]);
      if (!isNaN(min) && !isNaN(max)) {
        return { min, max };
      }
    }
    return null;
  }

  enableAndDisableSave(): void {
    this.maintenanceEntryForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.maintenanceEntryForm.valid
        ? this.templateService.setDisableAcceptButton(false)
        : this.templateService.setDisableAcceptButton(true);
    });
  }

  saveClicked(): void {
    this.dialogRef.onClose.pipe(takeUntil(this.destroy$)).subscribe((response) => {
      if (response.buttonType === DynamicDialogButtonType.Accept) {
        this.dialogRef.close(this.maintenanceEntryForm);
      }
    });
  }
}
